/**
 * CSS 单位转换工具函数
 * 根据项目的 rem 适配方案，将 px 转换为 rem
 *
 * 项目配置：
 * - 设计稿宽度：375px
 * - 换算基数：100px = 1rem
 * - 最大宽度：750px
 */

/**
 * 将 px 值转换为 rem 值
 * @param {number} px - 像素值
 * @param {number} [rootValue=100] - 换算基数，默认 100px = 1rem
 * @returns {string} rem 值字符串
 *
 * @example
 * calcCssUnit(100) // "1rem"
 * calcCssUnit(50) // "0.5rem"
 * calcCssUnit(200) // "2rem"
 * calcCssUnit(16, 16) // "1rem" (使用 16px = 1rem 的换算)
 */
export function calcCssUnit(px, rootValue = 100) {
    if (typeof px !== 'number' || px < 0) {
        console.warn('calcCssUnit: px 参数必须是大于等于 0 的数字')
        return '0rem'
    }

    if (typeof rootValue !== 'number' || rootValue <= 0) {
        console.warn('calcCssUnit: rootValue 参数必须是大于 0 的数字')
        rootValue = 100
    }

    const remValue = px / rootValue
    return `${remValue}rem`
}

/**
 * 将 px 值转换为 rem 值（使用项目默认配置）
 * @param {number} px - 像素值
 * @returns {string} rem 值字符串
 *
 * @example
 * pxToRem(100) // "1rem"
 * pxToRem(50) // "0.5rem"
 */
export function pxToRem(px) {
    return calcCssUnit(px, 100)
}

/**
 * 将 rem 值转换为 px 值
 * @param {number} rem - rem 值
 * @param {number} [rootValue=100] - 换算基数，默认 100px = 1rem
 * @returns {string} px 值字符串
 *
 * @example
 * remToPx(1) // "100px"
 * remToPx(0.5) // "50px"
 */
export function remToPx(rem, rootValue = 100) {
    if (typeof rem !== 'number' || rem < 0) {
        console.warn('remToPx: rem 参数必须是大于等于 0 的数字')
        return '0px'
    }

    if (typeof rootValue !== 'number' || rootValue <= 0) {
        console.warn('remToPx: rootValue 参数必须是大于 0 的数字')
        rootValue = 100
    }

    const pxValue = rem * rootValue
    return `${pxValue}px`
}

/**
 * 获取当前设备的 rem 基准值
 * @returns {number} 当前 html 元素的 font-size（px）
 */
export function getCurrentRemBase() {
    if (typeof window === 'undefined') {
        return 100 // 服务端渲染时返回默认值
    }

    const htmlElement = document.documentElement
    const computedStyle = window.getComputedStyle(htmlElement)
    const fontSize = parseFloat(computedStyle.fontSize)

    return fontSize || 100
}

/**
 * 根据当前设备动态计算 rem 值
 * @param {number} px - 像素值
 * @returns {string} 动态计算的 rem 值
 *
 * @example
 * // 如果当前设备 html font-size 是 80px
 * dynamicPxToRem(100) // "1.25rem"
 */
export function dynamicPxToRem(px) {
    const currentBase = getCurrentRemBase()
    return calcCssUnit(px, currentBase)
}

/**
 * 批量转换 px 到 rem
 * @param {Object} styles - 包含 px 值的样式对象
 * @param {number} [rootValue=100] - 换算基数
 * @returns {Object} 转换后的样式对象
 *
 * @example
 * batchPxToRem({
 *   width: 100,
 *   height: 200,
 *   fontSize: 16
 * })
 * // 返回：
 * // {
 * //   width: '1rem',
 * //   height: '2rem',
 * //   fontSize: '0.16rem'
 * // }
 */
export function batchPxToRem(styles, rootValue = 100) {
    const result = {}

    for (const [key, value] of Object.entries(styles)) {
        if (typeof value === 'number') {
            result[key] = calcCssUnit(value, rootValue)
        } else {
            result[key] = value
        }
    }

    return result
}
